import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  ChevronDown, Search, ArrowLeft, HelpCircle, CreditCard, Smartphone, Shield, 
  Users, Clock, FileText, Zap, Building, BarChart3, Camera, Calendar, 
  DollarSign, Settings, Bell, MapPin, Bot, Wrench, RefreshCw, CloudSun, 
  Package, HardDrive, Briefcase, AlertTriangle, CheckCircle, Home, Globe, 
  Layers, Monitor, Server
} from 'lucide-react';

// Icon mapping for dynamic icon rendering
const ICON_MAP = {
  HelpCircle, Zap, CreditCard, Building, Smartphone, MapPin, Users, Clock,
  FileText, DollarSign, BarChart3, Camera, Calendar, Bell, Bot, RefreshCw,
  HardDrive, CloudSun, Settings, Shield, Wrench, Package, Briefcase,
  AlertTriangle, CheckCircle, Home, Globe, Layers, Monitor, Server
};

const getIconComponent = (iconName) => {
  return ICON_MAP[iconName] || HelpCircle;
};

const FAQsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [openFaq, setOpenFaq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [faqs, setFaqs] = useState([]);

  // Fetch FAQs from backend
  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/faqs/web`);
      
      if (response.data.success) {
        const rawFaqs = response.data.faqs || [];
        
        // Process FAQs - group by sections
        const sections = [];
        const faqItems = [];
        let currentSection = null;
        
        rawFaqs.forEach(item => {
          if (item.type === 'section') {
            currentSection = {
              id: item.id,
              title: item.title,
              icon: item.icon || 'HelpCircle',
              slug: item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
            };
            sections.push(currentSection);
          } else if (item.type === 'faq' && currentSection) {
            faqItems.push({
              ...item,
              categoryId: currentSection.id,
              categorySlug: currentSection.slug
            });
          }
        });
        
        // Only include sections that have at least one FAQ
        const sectionsWithFaqs = sections.filter(section => 
          faqItems.some(faq => faq.categoryId === section.id)
        );
        
        // Build categories array with "All FAQs" at the start
        const categoryItems = [
          { id: 'all', slug: 'all', label: 'All FAQs', icon: 'HelpCircle' },
          ...sectionsWithFaqs.map(s => ({
            id: s.id,
            slug: s.slug,
            label: s.title,
            icon: s.icon
          }))
        ];
        
        setCategories(categoryItems);
        setFaqs(faqItems);
      }
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  // Filter FAQs based on search and category
  const filteredFaqs = faqs.filter(faq => {
    const matchesSearch = searchQuery === '' || 
      faq.question?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = activeCategory === 'all' || 
      faq.categoryId === activeCategory ||
      faq.categorySlug === activeCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Group filtered FAQs by category for display
  const groupedFaqs = categories
    .filter(cat => cat.id !== 'all')
    .map(category => ({
      ...category,
      faqs: filteredFaqs.filter(faq => faq.categoryId === category.id || faq.categorySlug === category.slug)
    }))
    .filter(group => group.faqs.length > 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{error}</p>
          <button 
            onClick={fetchFaqs}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link 
            to="/" 
            className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">How can we help you?</h1>
          <p className="text-blue-100 text-lg mb-8">Search our FAQ or browse categories below</p>
          
          {/* Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Categories */}
          <aside className="lg:w-72 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden sticky top-8">
              <div className="p-4 bg-gray-50 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Categories</h2>
              </div>
              <nav className="p-2">
                {categories.map(category => {
                  const IconComponent = getIconComponent(category.icon);
                  const isActive = activeCategory === category.id || activeCategory === category.slug;
                  const faqCount = category.id === 'all' 
                    ? faqs.length 
                    : faqs.filter(f => f.categoryId === category.id).length;
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <IconComponent className={`w-5 h-5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="flex-1 text-sm font-medium truncate">{category.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        isActive ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {faqCount}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* FAQ Content */}
          <div className="flex-1 min-w-0">
            {filteredFaqs.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                <HelpCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No FAQs found</h3>
                <p className="text-gray-500">
                  {searchQuery 
                    ? 'Try adjusting your search terms or browse all categories.'
                    : 'No FAQs are available in this category yet.'}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setActiveCategory('all');
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Clear Search
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-8">
                {activeCategory === 'all' ? (
                  // Show FAQs grouped by category
                  groupedFaqs.map(group => {
                    const IconComponent = getIconComponent(group.icon);
                    return (
                      <div key={group.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h2 className="font-semibold text-gray-900">{group.label}</h2>
                            <p className="text-sm text-gray-500">{group.faqs.length} question{group.faqs.length !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {group.faqs.map((faq, index) => {
                            const faqKey = `${group.id}-${index}`;
                            return (
                              <div key={faq.id || faqKey}>
                                <button
                                  onClick={() => setOpenFaq(openFaq === faqKey ? null : faqKey)}
                                  className="w-full px-6 py-4 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                                >
                                  <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                                  <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                                    openFaq === faqKey ? 'rotate-180' : ''
                                  }`} />
                                </button>
                                {openFaq === faqKey && (
                                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                                    <p className="text-gray-600 whitespace-pre-line">{faq.answer}</p>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  // Show FAQs for selected category without grouping
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {(() => {
                      const selectedCategory = categories.find(c => c.id === activeCategory || c.slug === activeCategory);
                      const IconComponent = selectedCategory ? getIconComponent(selectedCategory.icon) : HelpCircle;
                      return (
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h2 className="font-semibold text-gray-900">{selectedCategory?.label || 'FAQs'}</h2>
                            <p className="text-sm text-gray-500">{filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''}</p>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="divide-y divide-gray-200">
                      {filteredFaqs.map((faq, index) => (
                        <div key={faq.id || index}>
                          <button
                            onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                            className="w-full px-6 py-4 text-left flex items-start justify-between hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                            <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${
                              openFaq === faq.id ? 'rotate-180' : ''
                            }`} />
                          </button>
                          {openFaq === faq.id && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                              <p className="text-gray-600 whitespace-pre-line">{faq.answer}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            Can't find what you're looking for?{' '}
            <Link to="/support" className="text-blue-400 hover:text-blue-300 transition-colors">
              Contact Support
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default FAQsPage;
